import { Box } from '@factorialco/deprecated-design-system'
import { IndicatorsList, Widget } from '@factorialco/f0-react/dist/experimental'
import { AlertCircle, CheckCircle } from '@factorialco/f0-react/icons/app'
import { FormScoresDocument, type FormScoresQuery } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

type FormsScore = NonNullable<FormScoresQuery['forms']['scores']>[number]

const FormsScores = ({
  resourceId,
  resourceType,
  scores,
}: {
  resourceId: string
  resourceType: string
  scores: Record<string, string>
}) => {
  const scoreTypes = Object.keys(scores)
  const { data: loadedData } = useQuery(FormScoresDocument, {
    variables: {
      resourceId,
      resourceType,
      scoreTypes,
      formType: ['knowledge'],
    },
  })
  const results = loadedData?.forms.scores ?? []
  const knowledgeData = loadedData?.forms.testScoresConnection.nodes ?? []

  const allResults = [...results]
  if (knowledgeData.length > 0 && scores['courseknowledge']) {
    const knowledgeScoreResults =
      knowledgeData.reduce((acc, curr) => acc + curr.percentage, 0) / knowledgeData.length
    allResults.push({
      type: 'courseknowledge',
      numerator: knowledgeScoreResults,
      denominator: 100,
    })
  }
  const title = function (
    score: FormsScore | { type: string; numerator: number; denominator: string }
  ) {
    if (!scores[score.type]) {
      throw new Error(`Form score type "${score.type}" not found.`)
    }
    return scores[score.type]
  }
  const label = function (
    score: FormsScore | { type: string; numerator: number; denominator: string }
  ) {
    switch (score.denominator) {
      case 5: {
        if (score.numerator >= 4.5) return i18n.t('forms.default_rating_scale.very_high')
        if (score.numerator >= 3.5) return i18n.t('forms.default_rating_scale.high')
        if (score.numerator > 2.5) return i18n.t('forms.default_rating_scale.average')
        if (score.numerator > 1.5) return i18n.t('forms.default_rating_scale.low')
        return i18n.t('forms.default_rating_scale.very_low')
      }
      case 100: {
        if (score.numerator >= 80) return i18n.t('forms.default_rating_scale.very_high')
        if (score.numerator >= 60) return i18n.t('forms.default_rating_scale.high')
        if (score.numerator >= 40) return i18n.t('forms.default_rating_scale.average')
        if (score.numerator >= 20) return i18n.t('forms.default_rating_scale.low')
        return i18n.t('forms.default_rating_scale.very_low')
      }
      default: {
        throw new Error(
          `Unexpected denominator. Expected "5", got "${score.denominator}" in form score type "${score.type}"`
        )
      }
    }
  }
  const icon = function (
    score: FormsScore | { type: string; numerator: number; denominator: string }
  ) {
    switch (score.denominator) {
      case 5: {
        if (score.numerator > 2) return CheckCircle
        return AlertCircle
      }
      case 100: {
        if (score.numerator >= 80) return CheckCircle
        return AlertCircle
      }
      default: {
        throw new Error(
          `Unexpected denominator. Expected "5", got "${score.denominator}" in form score type "${score.type}"`
        )
      }
    }
  }
  const color = function (
    score: FormsScore | { type: string; numerator: number; denominator: string }
  ) {
    switch (score.denominator) {
      case 5: {
        if (score.numerator >= 3.5) return 'text-f1-icon-positive'
        if (score.numerator > 2) return 'text-f1-icon-warning'
        return 'text-f1-icon-critical'
      }
      case 100: {
        if (score.numerator >= 80) return 'text-f1-icon-positive'
        if (score.numerator >= 60) return 'text-f1-icon-warning'
        if (score.numerator >= 40) return 'text-f1-icon-warning'
        if (score.numerator >= 20) return 'text-f1-icon-critical'
        return 'text-f1-icon-critical'
      }
      default: {
        throw new Error(
          `Unexpected denominator. Expected "5", got "${score.denominator}" in form score type "${score.type}"`
        )
      }
    }
  }
  const scoreFormatter = new Intl.NumberFormat(i18n.locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })

  const formatContent = (
    score: FormsScore | { type: string; numerator: number; denominator: string }
  ) => {
    if (score.type === 'courseknowledge') {
      return `${scoreFormatter.format(score.numerator)}%`
    }
    return `${scoreFormatter.format(score.numerator)}/${score.denominator}`
  }
  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '1rem',
      }}
    >
      {allResults.map(
        (score: FormsScore | { type: string; numerator: number; denominator: string }) => (
          <Widget
            fullHeight
            key={score.type}
            header={{
              title: title(score),
            }}
          >
            <IndicatorsList
              items={[
                {
                  color: color(score),
                  content: formatContent(score),
                  icon: icon(score),
                  label: label(score),
                },
              ]}
            />
          </Widget>
        )
      )}
    </Box>
  )
}

export default FormsScores
